// Catálogos ordenados sobre TanStack Query. Mismo patrón que `useContenidos`:
// la cache de la query es la única fuente de verdad del listado y las
// mutaciones invalidan esa key en vez de parchear la lista a mano.
//
// Acá la invalidación NO es opcional: el alta puede RESTAURAR un ítem borrado,
// que vuelve con su `orden` original —en el medio del listado, no al final—,
// así que la única forma de mostrar el orden real es volver a pedirlo.
//
// El `recurso` entra por parámetro y forma parte de la query key: dos
// catálogos distintos nunca comparten cache.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  actualizarItemRequest,
  crearItemRequest,
  eliminarItemRequest,
  fetchCatalogo,
  type ItemDeCatalogoInput,
  type RecursoDeCatalogo,
} from "@/lib/catalogos";

export function catalogoKey(recurso: RecursoDeCatalogo) {
  return ["catalogo", recurso] as const;
}

export function useCatalogo(recurso: RecursoDeCatalogo) {
  return useQuery({
    queryKey: catalogoKey(recurso),
    queryFn: () => fetchCatalogo(recurso),
  });
}

export function useCrearItem(recurso: RecursoDeCatalogo) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ItemDeCatalogoInput) => crearItemRequest(recurso, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogoKey(recurso) }),
  });
}

export function useActualizarItem(recurso: RecursoDeCatalogo) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: ItemDeCatalogoInput & { id: number }) =>
      actualizarItemRequest(recurso, id, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogoKey(recurso) }),
  });
}

interface OpcionesDeBorrado {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/**
 * Los callbacks van a nivel `useMutation` y no en `mutate()` por el mismo
 * motivo que en `useEliminarContenido`: los de `mutate()` se descartan si el
 * diálogo se desmonta antes de que la mutación termine, y ahí el rechazo del
 * backend no produciría ninguna señal.
 */
export function useEliminarItem(recurso: RecursoDeCatalogo, opciones: OpcionesDeBorrado = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => eliminarItemRequest(recurso, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogoKey(recurso) });
      opciones.onSuccess?.();
    },
    onError: (error) => opciones.onError?.(error),
  });
}
